/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { get } from 'js/lib/user';

import { Button, Dialog, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { Loading } from '@coursera/coursera-ui';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import InternationalPhoneNumberInput from 'bundles/expression-of-interest/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { InternationalPhoneNumber } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  DEFAULT_REGION,
  newCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  isValidInternationalPhoneNumber,
  toRegionFromCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/utils';
import { SECTION_EVENT_MAP } from 'bundles/expression-of-interest/constants';
import { useAriaAnnouncement } from 'bundles/expression-of-interest/hooks/useAriaAnnouncement';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { updateUserProfile } from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  phone: Phone;
  isPhoneLoading: boolean;
  handleSubmit: () => Promise<void | {}>;
  navigateConfirmation: () => void;
  navigateSelectSubject: () => void;
  handlePhoneChange: (data?: ProfilePhone) => void;
  trackingProductId: string;
  eoiButtonSource?: EOIButtonSource;
  setRegion?: (region: string) => void;
};

const styles = {
  phoneInput: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
};

export const PhoneNumberSection = (props: Props): React.ReactElement => {
  const {
    handleSubmit,
    navigateConfirmation,
    navigateSelectSubject,
    phone,
    isPhoneLoading,
    handlePhoneChange,
    trackingProductId,
    eoiButtonSource,
    setRegion,
  } = props;

  const [fieldError, setFieldError] = useState<boolean>(false);
  const { announcement: ariaAnnouncement, announce } = useAriaAnnouncement();
  const { trackSubmitEoi } = useEoiEventing(trackingProductId);
  const phoneNumberRef = useRef<HTMLInputElement>(null);

  /**
   * Gets the region from the phone's country code, or returns the default region.
   */
  const getRegionFromPhone = () => {
    if (phone.countryCode) {
      const countryCode = newCountryCode(phone.countryCode);
      if (countryCode) {
        return toRegionFromCountryCode(countryCode) ?? DEFAULT_REGION;
      }
    }
    return DEFAULT_REGION;
  };

  /**
   * Checks if the phone number is invalid.
   * This checks both the fieldError state (set by phone component callback)
   * and also validates directly for cases where the callback hasn't fired yet
   * (e.g., when focus moves directly to the submit button).
   * Uses isValidInternationalPhoneNumber for consistent validation with the phone component.
   */
  const isPhoneInvalid = () => {
    // Check the state first (set by phone component's handleError callback)
    if (fieldError) return true;
    // Use the same validation as the phone component for consistency
    const region = getRegionFromPhone();
    return !isValidInternationalPhoneNumber(phone.phoneNumber as InternationalPhoneNumber, region, false);
  };

  /**
   * Announces the phone number validation error to screen readers.
   * Called on button focus and form submission when there's an error.
   */
  const announceError = () => {
    if (isPhoneInvalid()) {
      announce(_t('Invalid phone number'));
    }
  };

  // Announce error when button receives focus (so users know before submitting)
  const handleButtonFocus = () => {
    announceError();
  };

  const { productType } = useEoiOptimizationsContext();

  const baseTrackingData = {
    eoiSection: SECTION_EVENT_MAP.phoneNumberSection,
    eoiButtonSource,
  };

  const disclaimerText = _t(
    'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities.'
  );
  const titleCopy = _t('Add your phone number');
  const supportTextCopy = _t(
    'Our enrollment representatives may reach out to walk you through program details and provide personalized next steps.'
  );
  const submitButtonCopy = _t('Add phone number');

  const { HeadingGroup, Content, Actions } = Dialog;

  const navigateNext = () => {
    if (productType === ProductTypes.DEGREE_HUB) {
      navigateSelectSubject();
    } else {
      navigateConfirmation();
    }
  };

  const handlePhoneSubmit = () => {
    if (phone.phoneNumber === '') {
      setFieldError(true);
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
      // Announce error for empty phone number
      announce(_t('Invalid phone number'));
    } else if (fieldError) {
      // Announce error for invalid phone number format
      announce(_t('Invalid phone number'));
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
    } else if (!fieldError) {
      handleSubmit()
        .then(() => {
          const formattedPhone = phone;
          // user profile expects the phone number to be stripped of its country code
          formattedPhone.phoneNumber = phone.phoneNumber.replace(`+${phone.countryCode}`, '');
          const currentUser = get();
          updateUserProfile({
            userId: currentUser.id,
            externalId: currentUser.external_id,
            fullname: currentUser.fullName,
            phone: formattedPhone,
          });

          navigateNext();
          trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'success' });
        })
        .catch(() => {
          trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'api_failure' });
        });
    }
  };

  if (isPhoneLoading) {
    return (
      <Content>
        <Loading />
      </Content>
    );
  }

  return (
    <>
      <HeadingGroup supportText={supportTextCopy}>{titleCopy}</HeadingGroup>
      <Content>
        <div css={styles.phoneInput}>
          <InternationalPhoneNumberInput
            profile={{ phone }}
            isOptional={false}
            isEmptyValid={false}
            customStyles={customFormattedPhoneStyles}
            submissionError={fieldError}
            handleFormChange={handlePhoneChange}
            setRegion={setRegion}
            handleError={(hasError: boolean) => {
              setFieldError(hasError);
            }}
            ref={phoneNumberRef}
            ariaDescribedBy="phone-consent-add"
          />
        </div>

        <Typography2 component="p" variant="bodySecondary" color="supportText" id="phone-consent-add">
          {disclaimerText}
        </Typography2>
      </Content>
      <Actions>
        <Button onClick={handlePhoneSubmit} onFocus={handleButtonFocus} css={styles.primaryButton}>
          {submitButtonCopy}
        </Button>
      </Actions>
      {/* Screen reader announcement for validation errors */}
      <VisuallyHidden>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {ariaAnnouncement}
        </div>
      </VisuallyHidden>
    </>
  );
};

export default PhoneNumberSection;
